import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { listResponses } from '../../lib/services/responses'
import { AlertTriangle, Send, ChevronLeft } from 'lucide-react'
import { authFailureMessage, isAuthFailure, shouldRetryRequest } from '../../lib/errors'
import { useAuth } from '../../context/AuthContext'

/**
 * Draft Findings Review [FE.5.1]
 * Reviewer Gate: Surveyor must review all findings before releasing.
 */
export function FindingsReviewPage() {
    const { visitId } = useParams()
    const navigate = useNavigate()
    const { signOut } = useAuth()

    const { data: responses, error, isLoading } = useQuery({
        queryKey: ['visit-responses', visitId],
        queryFn: () => listResponses(visitId!),
        enabled: Boolean(visitId),
        retry: shouldRetryRequest,
    })

    const findings =
        responses?.map((response) => ({
            id: response.template_id,
            status: response.response_value === 'compliant' ? 'Compliant' : 'Non-Compliant',
            notes: response.notes || 'No notes provided.',
        })) || []

    const handleRelease = () => {
        alert('Visit released to facility for comment.')
        navigate('/visits')
    }

    return (
        <div className="mx-auto max-w-4xl space-y-5 pb-24">
            <header className="app-surface p-5">
                <div className="flex items-center gap-2">
                    <button onClick={() => navigate(-1)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100">
                        <ChevronLeft size={20} />
                    </button>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Visit Workflow</p>
                        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Review Findings</h1>
                    </div>
                </div>
                {/* Tab indicators removed — Overview/Checklist/Evidence were non-functional visual labels */}
            </header>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={20} />
                    <div>
                        <p className="text-sm font-bold text-amber-900">Final Surveyor Review</p>
                        <p className="text-xs text-amber-800">
                            Ensure no PHI is present in notes. Once released, the facility will be able to view these findings.
                        </p>
                    </div>
                </div>
            </div>

            <div className="app-surface overflow-hidden">
                <div className="hidden grid-cols-[1fr_auto] border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 md:grid">
                    <span>Checklist Item</span>
                    <span>Status</span>
                </div>
                {Boolean(error) && (
                    <div className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                        <p className="text-sm font-semibold">
                            {isAuthFailure(error) ? 'Session validation failed for findings' : 'Failed to load findings'}
                        </p>
                        <p className="text-xs">
                            {isAuthFailure(error) ? authFailureMessage(error) : (error as Error).message}
                        </p>
                        {isAuthFailure(error) && (
                            <button
                                className="mt-1 text-xs underline"
                                onClick={async () => {
                                    await signOut()
                                    navigate('/login')
                                }}
                            >
                                Sign in again
                            </button>
                        )}
                    </div>
                )}
                {findings.map((finding) => (
                    <div key={finding.id} className="border-b border-slate-100 px-4 py-3">
                        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <span className="text-xs font-bold text-neutral-400">
                                <span className="mr-1 md:hidden">Checklist Item:</span>
                                Standard {finding.id}
                            </span>
                            <span className={`w-fit rounded px-2 py-1 text-xs font-bold ${finding.status === 'Compliant' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                {finding.status}
                            </span>
                        </div>
                        <p className="text-sm text-neutral-700">{finding.notes}</p>
                    </div>
                ))}
                {isLoading && (
                    <div className="p-4 text-sm text-slate-500">
                        Loading submitted findings...
                    </div>
                )}
                {!isLoading && findings.length === 0 && (
                    <div className="p-4 text-sm text-neutral-500">
                        No submitted findings yet.
                    </div>
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 border-t border-neutral-200 bg-white p-4 md:relative md:border-0 md:bg-transparent md:p-0">
                <button
                    onClick={handleRelease}
                    className="btn-primary flex w-full items-center justify-center space-x-2 py-4"
                >
                    <Send size={18} />
                    <span>Release to Facility</span>
                </button>
            </div>
        </div>
    )
}
